<template>
  <div>
    <b-row class="mb-2">
      <b-col cols="12">
        <b-button
          v-if="hasPermission('privacy-gap', 'create')"
          variant="primary"
          size="sm"
          @click="showAddModal = true"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          Agregar Obligación
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-table
          :items="obligations"
          :fields="tableColumns"
          striped
          hover
          responsive
          :busy="isLoading"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle" />
              <strong class="ml-2">Cargando...</strong>
            </div>
          </template>

          <template #cell(order)="data">
            <strong>{{ data.item.order }}</strong>
          </template>

          <template #cell(name)="data">
            {{ data.item.name }}
          </template>

          <template #cell(article)="data">
            <b-badge variant="light-info">
              {{ data.item.article }}
            </b-badge>
          </template>

          <template #cell(description)="data">
            <span class="text-truncate d-inline-block" style="max-width: 300px;">
              {{ data.item.description }}
            </span>
          </template>

          <template #cell(actions)="data">
            <div class="d-flex">
              <b-button
                v-if="hasPermission('privacy-gap', 'edit')"
                variant="flat-secondary"
                size="sm"
                @click="editObligation(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="hasPermission('privacy-gap', 'delete')"
                variant="flat-danger"
                size="sm"
                class="ml-1"
                v-b-modal="`delete-modal-${data.item._id}`"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>

              <b-modal
                :id="`delete-modal-${data.item._id}`"
                centered
                no-close-on-backdrop
                modal-class="modal-danger"
                ok-variant="danger"
                cancel-variant="outline-secondary"
                title="Confirmar Eliminación"
                ok-title="Eliminar"
                cancel-title="Cancelar"
                @ok="deleteObligation(data.item)"
              >
                <p>
                  ¿Está seguro de eliminar esta obligación?
                </p>
                <b-alert variant="warning" show>
                  <strong>Advertencia:</strong> Esto eliminará también todos los registros de brechas asociados a esta obligación.
                </b-alert>
              </b-modal>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-modal
      v-model="showAddModal"
      title="Agregar Obligación"
      size="lg"
      no-close-on-backdrop
      @ok="handleAddObligation"
    >
      <validation-observer ref="addFormRef" tag="form">
        <b-row>
          <b-col cols="12">
            <b-form-group label="Orden *" label-for="order">
              <validation-provider name="Orden" rules="required" v-slot="{ errors }">
                <b-form-input
                  v-model.number="newObligation.order"
                  type="number"
                  placeholder="Ej: 1"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Nombre *" label-for="name">
              <validation-provider name="Nombre" rules="required" v-slot="{ errors }">
                <b-form-input
                  v-model="newObligation.name"
                  placeholder="Ej: Deber de información al titular"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Artículo *" label-for="article">
              <validation-provider name="Artículo" rules="required" v-slot="{ errors }">
                <b-form-input
                  v-model="newObligation.article"
                  placeholder="Ej: Art. 12"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Descripción *" label-for="description">
              <validation-provider name="Descripción" rules="required" v-slot="{ errors }">
                <b-form-textarea
                  v-model="newObligation.description"
                  rows="4"
                  placeholder="Descripción detallada de la obligación"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>

    <b-modal
      v-model="showEditModal"
      title="Editar Obligación"
      size="lg"
      no-close-on-backdrop
      @ok="handleEditObligation"
    >
      <validation-observer ref="editFormRef" tag="form">
        <b-row>
          <b-col cols="12">
            <b-form-group label="Orden *" label-for="order">
              <validation-provider name="Orden" rules="required" v-slot="{ errors }">
                <b-form-input
                  v-model.number="editingObligation.order"
                  type="number"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Nombre *" label-for="name">
              <validation-provider name="Nombre" rules="required" v-slot="{ errors }">
                <b-form-input v-model="editingObligation.name" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Artículo *" label-for="article">
              <validation-provider name="Artículo" rules="required" v-slot="{ errors }">
                <b-form-input v-model="editingObligation.article" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Descripción *" label-for="description">
              <validation-provider name="Descripción" rules="required" v-slot="{ errors }">
                <b-form-textarea
                  v-model="editingObligation.description"
                  rows="4"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import { ref, onMounted } from '@vue/composition-api'
import { BSpinner, BBadge, VBModal, BTable, BAlert } from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import i18n from '@/libs/i18n'
import store from '@/store'
import { required } from '@validations'

export default {
  name: 'ObligationManager',
  components: {
    BSpinner,
    BBadge,
    BTable,
    BAlert,
    ValidationObserver,
    ValidationProvider,
  },
  directives: {
    'b-modal': VBModal,
  },
  setup(props, { emit }) {
    const { getItemsAggregate, createItem, updateItem, updateItems, deleteItem } = useLambda()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { client } = store.state?.userStore?.userData

    const obligations = ref([])
    const isLoading = ref(true)
    const showAddModal = ref(false)
    const showEditModal = ref(false)
    const addFormRef = ref(null)
    const editFormRef = ref(null)

    const newObligation = ref({
      order: null,
      name: '',
      article: '',
      description: '',
    })

    const editingObligation = ref({
      _id: null,
      order: null,
      name: '',
      article: '',
      description: '',
    })

    const tableColumns = [
      { key: 'order', label: 'N°', sortable: true },
      { key: 'name', label: 'Nombre', sortable: true },
      { key: 'article', label: 'Artículo', sortable: true },
      { key: 'description', label: 'Descripción' },
      { key: 'actions', label: 'Acciones' },
    ]

    const fetchObligations = async () => {
      isLoading.value = true
      try {
        const pipeline = [
          { $match: { deleted: { $ne: true } } },
          { $sort: { order: 1 } }
        ]

        const result = await getItemsAggregate({
          collection: 'gapObligation',
          pipeline,
        })

        obligations.value = result || []
      } catch (error) {
        console.log(error)
        showErrorMessage('Error al cargar obligaciones')
      } finally {
        isLoading.value = false
      }
    }

    const handleAddObligation = async (bvModalEvent) => {
      bvModalEvent.preventDefault()

      if (!hasPermission('privacy-gap', 'create')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }

      const valid = await addFormRef.value.validate()
      if (!valid) return

      try {
        const payload = {
          ...newObligation.value,
          createdAt: new Date(),
        }

        await createItem({
          collection: 'gapObligation',
          payload,
        })

        showSuccessMessage('Obligación agregada correctamente')
        showAddModal.value = false
        newObligation.value = {
          order: null,
          name: '',
          article: '',
          description: '',
        }
        fetchObligations()
        emit('updated')
      } catch (error) {
        console.log(error)
        showErrorMessage('Error al agregar obligación')
      }
    }

    const editObligation = (obligation) => {
      editingObligation.value = {
        _id: obligation._id,
        order: obligation.order,
        name: obligation.name,
        article: obligation.article,
        description: obligation.description,
      }
      showEditModal.value = true
    }

    const handleEditObligation = async (bvModalEvent) => {
      bvModalEvent.preventDefault()

      if (!hasPermission('privacy-gap', 'edit')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }

      const valid = await editFormRef.value.validate()
      if (!valid) return

      try {
        const query = { _id: oid(editingObligation.value._id) }
        const action = {
          $set: {
            order: editingObligation.value.order,
            name: editingObligation.value.name,
            article: editingObligation.value.article,
            description: editingObligation.value.description,
            updatedAt: new Date(),
          },
        }

        await updateItem({
          collection: 'gapObligation',
          query,
          action,
        })

        showSuccessMessage('Obligación actualizada correctamente')
        showEditModal.value = false
        fetchObligations()
        emit('updated')
      } catch (error) {
        console.log(error)
        showErrorMessage('Error al actualizar obligación')
      }
    }

    const deleteObligation = async (obligation) => {
      if (!hasPermission('privacy-gap', 'delete')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      try {
        const query = { _id: oid(obligation._id) }
        const action = {
          $set: {
            deleted: true,
          },
        }

        await updateItem({
          collection: 'gapObligation',
          query,
          action,
        })

        // Also soft-delete all associated gap records — scoped to this client only,
        // since gapObligation is a global catalog shared across clients but `gap` is not.
        const gapQuery = { obligation_id: oid(obligation._id), client_id: oid(client.$oid) }
        const gapAction = {
          $set: {
            deleted: true,
          },
        }

        await updateItems({
          collection: 'gap',
          query: gapQuery,
          action: gapAction,
        })

        showSuccessMessage('Obligación eliminada correctamente')
        fetchObligations()
        emit('updated')
      } catch (error) {
        console.log(error)
        showErrorMessage('Error al eliminar obligación')
      }
    }

    onMounted(() => {
      fetchObligations()
    })

    return {
      obligations,
      isLoading,
      tableColumns,
      showAddModal,
      showEditModal,
      addFormRef,
      editFormRef,
      newObligation,
      editingObligation,
      handleAddObligation,
      editObligation,
      handleEditObligation,
      deleteObligation,
      required,
      hasPermission,
    }
  },
}
</script>
